/**
 * json-render Components Registry
 * 
 * Renders registered UI components from JSON metadata.
 * Used to display dynamic content in agent chat messages.
 */

import React from 'react';
import PlanAccordion from './PlanAccordion';
import ProgressIndicator from './ProgressIndicator';
import PRPreviewCard from './PRPreviewCard';
import CodePanel from './CodePanel';
import ClarificationBanner from './ClarificationBanner';
import SubTaskList from './SubTaskList';
import TerminalOutput from './TerminalOutput';
import ApprovalButton from './ApprovalButton';
import MarkdownRenderer from './MarkdownRenderer';
import DiffSummary from './DiffSummary';
import ExecutionQualityCard from './ExecutionQualityCard';
import MonitoringPreplanClarification from './MonitoringPreplanClarification';
import VerificationUpdate from './VerificationUpdate';

import type {
    JsonRenderComponent,
    JsonRenderComponentType,
    PRPreviewCardProps,
} from '../../types/json-render';
import type { ClarificationActionMode, ClarificationSelectionDraft } from '../../types/json-render';

// Component registry - maps type to React component
const componentRegistry: Partial<Record<JsonRenderComponentType, React.ComponentType<any>>> = {
    plan_accordion: PlanAccordion,
    progress_indicator: ProgressIndicator,
    pr_preview_card: PRPreviewCard,
    code_panel: CodePanel,
    clarification_banner: ClarificationBanner,
    sub_task_list: SubTaskList,
    terminal_output: TerminalOutput,
    approval_button: ApprovalButton,
    markdown: MarkdownRenderer,
    diff_summary: DiffSummary,
    execution_quality_card: ExecutionQualityCard,
    monitoring_preplan: MonitoringPreplanClarification,
    verification_update: VerificationUpdate,
};


interface JsonRenderMessageProps {
    /** Array of json-render components to display */
    components: JsonRenderComponent[];
    /** Markdown fallback when components are malformed or non-renderable */
    fallbackMarkdown?: string;
    /** Callback when user approves a plan */
    onPlanApprove?: (planId: string) => void;
    /** Callback when user clicks PR button */
    onPRClick?: (prUrl: string) => void;
    /** Callback when user clicks a clarification option */
    onClarificationSubmit?: (option: {
        value: string;
        label?: string;
        actionId?: string;
        expiresAt?: string;
        variant?: string;
        selectedIssueIds?: string[];
        selectedScopePaths?: string[];
        selectedBatchId?: string;
        selectedGoal?: 'performance' | 'security' | 'debugging' | 'stability';
        monitoringId?: string;
        monitoringUrl?: string;
    }) => void;
    /** Active action tokens from latest status poll */
    activeClarificationActionIds?: string[];
    /** Active clarification action values from latest status poll */
    activeClarificationActionValues?: string[];
    /** Clarification action request in-flight */
    isClarificationLoading?: boolean;
    /** Current action id being processed */
    loadingClarificationActionId?: string | null;
    /** Force disable clarification actions regardless of local option state */
    clarificationForceDisabled?: boolean;
    /** Optional disabled reason shown as button tooltip */
    clarificationDisabledReason?: string;
    /** Overlay is active for the current clarification context */
    suppressActiveClarificationUi?: boolean;
    /** Updates local selection draft for overlay submission */
    onClarificationSelectionChange?: (draft: ClarificationSelectionDraft) => void;
}

interface JsonRenderBoundaryProps {
    componentType: string;
    children: React.ReactNode;
}

interface JsonRenderBoundaryState {
    hasError: boolean;
}

class JsonRenderComponentBoundary extends React.Component<JsonRenderBoundaryProps, JsonRenderBoundaryState> {
    state: JsonRenderBoundaryState = { hasError: false };

    static getDerivedStateFromError(): JsonRenderBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error) {
        console.warn(`Failed to render json-render component "${this.props.componentType}"`, error);
    }

    render() {
        if (this.state.hasError) {
            return null;
        }
        return this.props.children;
    }
}

/**
 * Renders an array of json-render components
 */
export default function JsonRenderMessage({
    components,
    fallbackMarkdown,
    onPlanApprove,
    onPRClick,
    onClarificationSubmit,
    activeClarificationActionIds = [],
    activeClarificationActionValues = [],
    isClarificationLoading = false,
    loadingClarificationActionId = null,
    clarificationForceDisabled = false,
    clarificationDisabledReason,
    suppressActiveClarificationUi = false,
    onClarificationSelectionChange
}: JsonRenderMessageProps) {
    if (!components || components.length === 0) {
        if (fallbackMarkdown && fallbackMarkdown.trim().length > 0) {
            return <MarkdownRenderer content={fallbackMarkdown} />;
        }
        return null;
    }

    const clarificationComponentTypes = new Set<JsonRenderComponentType>([
        'clarification_banner',
        'monitoring_preplan',
        'verification_update'
    ]);
    const activeActionIdSet = new Set(activeClarificationActionIds);
    const activeActionValueSet = new Set(
        activeClarificationActionValues
            .filter((value): value is string => typeof value === 'string')
            .map((value) => value.trim())
            .filter((value) => value.length > 0)
    );
    const resolveComponentActionMode = (component: JsonRenderComponent): ClarificationActionMode => {
        if (!clarificationComponentTypes.has(component.type)) {
            return 'full';
        }
        const allowActionsWithoutActiveTokens =
            component.type === 'verification_update'
            && (component.props as any)?.allowActionsWithoutActiveTokens === true;
        const options = component.type === 'clarification_banner'
            ? (Array.isArray((component.props as any)?.options) ? (component.props as any).options : [])
            : (Array.isArray((component.props as any)?.actions) ? (component.props as any).actions : []);
        const normalizedOptions = options.map((opt: any) => ({
            actionId: typeof opt?.actionId === 'string' ? opt.actionId.trim() : '',
            value: typeof opt?.value === 'string' ? opt.value.trim() : ''
        }));
        const hasActionIdBackedOption = normalizedOptions.some((entry: { actionId: string }) => entry.actionId.length > 0);
        const hasKnownActiveActionSet = activeActionIdSet.size > 0 || activeActionValueSet.size > 0;
        if (allowActionsWithoutActiveTokens && normalizedOptions.length > 0) {
            if (suppressActiveClarificationUi) {
                return 'selection_only';
            }
            return 'full';
        }
        if (hasActionIdBackedOption && !hasKnownActiveActionSet) {
            return 'read_only';
        }
        const hasAnyActiveAction = hasActionIdBackedOption
            ? (
                normalizedOptions.some((entry: { actionId: string }) => entry.actionId.length > 0 && activeActionIdSet.has(entry.actionId))
                || normalizedOptions.some((entry: { actionId: string; value: string }) => entry.actionId.length === 0 && entry.value.length > 0 && activeActionValueSet.has(entry.value))
            )
            : normalizedOptions.some((entry: { value: string }) => entry.value.length > 0 && activeActionValueSet.has(entry.value));

        if (!hasAnyActiveAction) {
            return 'read_only';
        }
        if (suppressActiveClarificationUi) {
            return component.type === 'clarification_banner' ? 'read_only' : 'selection_only';
        }
        return 'full';
    };

    const renderableComponents = components.filter((component): component is JsonRenderComponent => {
        return !!component
            && typeof component.type === 'string'
            && typeof component.id === 'string'
            && component.id.trim().length > 0
            && !!component.props
            && typeof component.props === 'object';
    });
    const renderedNodes: React.ReactNode[] = [];

    for (const component of renderableComponents) {
        const Component = componentRegistry[component.type];

        if (!Component) {
            console.warn(`Unknown json-render component type: ${component.type}`);
            continue;
        }

        const extraProps: Record<string, any> = {};
        if (component.type === 'plan_accordion' && onPlanApprove) {
            extraProps.onApprove = () => onPlanApprove(component.id);
        }
        if (component.type === 'pr_preview_card' && onPRClick) {
            const prCardProps = component.props as unknown as PRPreviewCardProps & { prUrl?: string };
            if (typeof prCardProps.prUrl === 'string' && prCardProps.prUrl.trim() !== '') {
                extraProps.onPublish = () => onPRClick(prCardProps.prUrl as string);
            }
        }
        const clarificationActionMode = resolveComponentActionMode(component);
        if (clarificationComponentTypes.has(component.type)) {
            extraProps.actionMode = clarificationActionMode;
        }
        if (component.type === 'clarification_banner' && onClarificationSubmit) {
            extraProps.onSubmit = onClarificationSubmit;
            extraProps.activeActionIds = activeClarificationActionIds;
            extraProps.isLoading = isClarificationLoading;
            extraProps.loadingActionId = loadingClarificationActionId;
            extraProps.forceDisabled = clarificationForceDisabled;
            extraProps.disabledReason = clarificationDisabledReason;
        }
        if (component.type === 'verification_update' && onClarificationSubmit) {
            extraProps.onSubmit = onClarificationSubmit;
            extraProps.activeActionIds = activeClarificationActionIds;
            extraProps.isLoading = isClarificationLoading;
            extraProps.loadingActionId = loadingClarificationActionId;
            extraProps.forceDisabled = clarificationForceDisabled;
            extraProps.disabledReason = clarificationDisabledReason;
        }
        if (component.type === 'monitoring_preplan' && onClarificationSubmit) {
            extraProps.onSubmit = onClarificationSubmit;
            extraProps.activeActionIds = activeClarificationActionIds;
            extraProps.isLoading = isClarificationLoading;
            extraProps.loadingActionId = loadingClarificationActionId;
            extraProps.forceDisabled = clarificationForceDisabled;
            extraProps.disabledReason = clarificationDisabledReason;
            extraProps.onSelectionChange = clarificationActionMode === 'read_only' || !onClarificationSelectionChange
                ? undefined
                : (draft: ClarificationSelectionDraft) =>
                    onClarificationSelectionChange({
                        ...draft,
                        sourceContextId: component.id
                    });
        }
        renderedNodes.push(
            <JsonRenderComponentBoundary key={component.id} componentType={component.type}>
                <Component
                    {...component.props}
                    {...extraProps}
                />
            </JsonRenderComponentBoundary>
        );
    }

    if (renderedNodes.length === 0) {
        if (fallbackMarkdown && fallbackMarkdown.trim().length > 0) {
            return <MarkdownRenderer content={fallbackMarkdown} />;
        }
        return null;
    }

    return <div className="space-y-3">{renderedNodes}</div>;
}

// Export individual components for direct use
export { PlanAccordion, ProgressIndicator, PRPreviewCard, CodePanel };
